import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Textarea,
  Input,
  Label,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@tonic/shared/components/ui';
import {
  ArrowLeft,
  Clock,
  User,
  Recycle,
  ClipboardCheck,
  Package,
  Calendar,
  MessageSquare,
  History,
  Camera,
  FileText,
  MoreVertical,
  Send,
  Phone,
  Mail,
  Scale,
  Truck,
} from 'lucide-react';

const mockRecycleDetail = {
  id: "REC00789456",
  status: "Processing",
  priority: "Medium",
  customer: {
    id: "CUST456",
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    phone: "+1 (555) 987-6543",
    address: "456 Oak Ave, Somewhere, ST 54321",
    membershipLevel: "IKEA Family",
    previousRecycles: 5
  },
  items: {
    id: "MALM-70263832",
    name: "MALM Bed Frame",
    category: "Bedroom Furniture",
    purchaseDate: "2022-08-10",
    materials: ["Wood", "Metal", "Particleboard"],
    condition: "Used - Good",
    dimensions: "160x200 cm",
    weight: "65 kg"
  },
  recycling: {
    program: "IKEA Furniture Take-Back",
    type: "Furniture Recycling",
    description: "Customer returning bed frame for recycling. Item is in good condition but customer is moving abroad.",
    requestDate: "2024-03-15T09:30:00Z",
    estimatedCompletion: "2024-03-18",
    assignedHandler: {
      id: "HAND001",
      name: "Lisa Chen",
      specialization: "Furniture Processing",
      rating: 4.9,
      processedItems: 245
    },
    location: "Recycling Center - Zone B",
    compensation: {
      baseValue: 45.00,
      bonusPoints: 500,
      total: 45.00
    }
  },
  notes: [
    {
      id: "NOTE001",
      author: "Lisa Chen",
      timestamp: "2024-03-15T10:00:00Z",
      content: "Initial inspection completed. Item suitable for recycling program. All parts present.",
      type: "inspection"
    },
    {
      id: "NOTE002",
      author: "Tom Baker",
      timestamp: "2024-03-15T11:30:00Z",
      content: "Item scheduled for processing tomorrow. Materials separation planned.",
      type: "logistics"
    }
  ],
  photos: [
    {
      id: "PHOTO001",
      url: "/photos/recycle/001.jpg",
      timestamp: "2024-03-15T10:00:00Z",
      description: "Full item view"
    },
    {
      id: "PHOTO002",
      url: "/photos/recycle/002.jpg",
      timestamp: "2024-03-15T10:02:00Z",
      description: "Material labels and markings"
    }
  ],
  documents: [
    {
      id: "DOC001",
      name: "Original Purchase Receipt",
      type: "receipt",
      uploadedAt: "2024-03-15T09:35:00Z"
    },
    {
      id: "DOC002",
      name: "Item Declaration Form",
      type: "declaration",
      uploadedAt: "2024-03-15T09:35:00Z"
    }
  ],
  progressSteps: [
    { label: "Received", completed: true, timestamp: "2024-03-15T09:30:00Z" },
    { label: "Inspection", completed: true, timestamp: "2024-03-15T10:00:00Z" },
    { label: "Sorting", completed: true, timestamp: "2024-03-15T11:30:00Z" },
    { label: "Processing", completed: true, timestamp: "2024-03-15T14:00:00Z" },
    { label: "Material Recovery", completed: false },
    { label: "Completed", completed: false }
  ],
  timeline: [
    {
      id: "EVENT001",
      type: "status_change",
      description: "Recycling request received",
      timestamp: "2024-03-15T09:30:00Z",
      actor: "System"
    },
    {
      id: "EVENT002",
      type: "inspection",
      description: "Initial inspection completed",
      timestamp: "2024-03-15T10:00:00Z",
      actor: "Lisa Chen"
    },
    {
      id: "EVENT003",
      type: "processing",
      description: "Item moved to processing zone",
      timestamp: "2024-03-15T11:30:00Z",
      actor: "Tom Baker"
    }
  ]
};

function getStatusColor(status) {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'default';
    case 'processing':
      return 'secondary';
    case 'pending':
      return 'outline';
    case 'rejected':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getPriorityColor(priority) {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'outline';
    default:
      return 'outline';
  }
}

export function RecycleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('details');
  const [newNote, setNewNote] = React.useState('');

  // In a real app, we would fetch the recycle details using the id
  const recycle = mockRecycleDetail;

  const handleStatusChange = (newStatus) => {
    console.log("Status changed to:", newStatus);
    // Update status logic here
  };

  const handleNotesUpdate = (newNotes) => {
    console.log("Notes updated:", newNotes);
    // Update notes logic here
  };

  const handleBackClick = () => {
    navigate('/admin/re-commerce/recycle/list');
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      console.log("Adding note:", newNote);
      setNewNote('');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Recycle #{recycle.id}</h1>
            <p className="text-sm text-muted-foreground">{recycle.items.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={getPriorityColor(recycle.priority)}>
            {recycle.priority}
          </Badge>
          <Badge variant={getStatusColor(recycle.status)}>
            {recycle.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Print Details</DropdownMenuItem>
              <DropdownMenuItem>Export Data</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">Cancel Request</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="materials">Materials</TabsTrigger>
          <TabsTrigger value="processing">Processing</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="photos">Photos</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{recycle.customer.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{recycle.customer.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{recycle.customer.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span>Previous Recycles: {recycle.customer.previousRecycles}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Item Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{recycle.items.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Scale className="w-4 h-4 text-muted-foreground" />
                    <span>Weight: {recycle.items.weight}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>Purchase Date: {recycle.items.purchaseDate}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Recycle className="w-4 h-4 text-muted-foreground" />
                    <span>Condition: {recycle.items.condition}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Processing Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-muted-foreground" />
                    <span>Location: {recycle.recycling.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>Handler: {recycle.recycling.assignedHandler.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>Est. Completion: {recycle.recycling.estimatedCompletion}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Compensation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Base Value:</span>
                    <span>${recycle.recycling.compensation.baseValue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Bonus Points:</span>
                    <span>{recycle.recycling.compensation.bonusPoints}</span>
                  </div>
                  <div className="flex justify-between items-center font-bold">
                    <span>Total Value:</span>
                    <span>${recycle.recycling.compensation.total.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="materials">
            <Card>
              <CardHeader>
                <CardTitle>Material Composition</CardTitle>
                <CardDescription>Breakdown of materials for recycling</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recycle.items.materials.map((material, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <span className="font-medium">{material}</span>
                      <Badge variant="outline">Processing</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="processing">
            <Card>
              <CardHeader>
                <CardTitle>Processing Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {recycle.progressSteps.map((step, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                        }`}>
                        <ClipboardCheck className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{step.label}</p>
                        {step.timestamp && (
                          <p className="text-sm text-muted-foreground">
                            {new Date(step.timestamp).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notes">
            <Card>
              <CardHeader>
                <CardTitle>Notes & Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Textarea
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                    />
                    <Button onClick={handleAddNote}>
                      <Send className="w-4 h-4 mr-2" />
                      Add Note
                    </Button>
                  </div>
                  <div className="space-y-4 mt-6">
                    {recycle.notes.map((note) => (
                      <div key={note.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{note.author}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(note.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="outline" className="capitalize">
                            {note.type}
                          </Badge>
                        </div>
                        <p>{note.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="photos">
            <Card>
              <CardHeader>
                <CardTitle>Photos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recycle.photos.map((photo) => (
                    <div key={photo.id} className="border rounded-lg overflow-hidden">
                      <div className="aspect-video bg-gray-100 relative">
                        <Camera className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400" />
                      </div>
                      <div className="p-4">
                        <p className="font-medium">{photo.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(photo.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recycle.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Uploaded: {new Date(doc.uploadedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {doc.type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {recycle.timeline.map((event) => (
                    <div key={event.id} className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <History className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{event.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {event.actor} - {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
